import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { ProductCard } from "./ProductCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SectionHead = ({ kicker, title, note, linkTo, linkLabel, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14"
  >
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-military font-semibold mb-4">{kicker}</p>
      <h2 className="font-serif text-4xl md:text-6xl text-military-dark tracking-tight">{title}</h2>
    </div>
    <div className="flex flex-col md:items-end gap-4">
      {note && <p className="text-sm text-military-dark/60 max-w-xs">{note}</p>}
      {linkTo && (
        <Link to={linkTo} data-testid={testid} className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-military-dark hover:text-military transition-colors">
          {linkLabel} <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      )}
    </div>
  </motion.div>
);

export const HomeSections = () => {
  const [products, setProducts] = useState(null);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => setProducts([]));
    axios.get(`${API}/brands`).then((r) => setBrands(r.data)).catch(() => {});
  }, []);

  if (!products) return null;
  const launches = products.filter((p) => p.is_new).slice(0, 4);
  const offers = products.filter((p) => p.sale_price != null && p.sale_price < p.price).slice(0, 4);
  const brandsWithProducts = brands.filter((b) => b.product_count > 0);

  return (
    <>
      {brandsWithProducts.length > 0 && (
        <section id="marcas" data-testid="home-brands-section" className="bg-offwhite py-24 md:py-32 border-b border-military/10">
          <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
            <SectionHead
              kicker="Compre por marca"
              title={<>As marcas que a gente <span className="italic font-light text-military">confia.</span></>}
              note="Curadoria de fabricantes com procedência e qualidade."
              linkTo="/marcas"
              linkLabel="Ver todas as marcas"
              testid="home-brands-all"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {brandsWithProducts.map((b, i) => (
                <motion.div key={b.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.06 }}>
                  <Link
                    to={`/marca/${b.slug}`}
                    data-testid={`home-brand-${b.slug}`}
                    className="group flex items-center justify-between gap-3 bg-white border border-military/10 rounded-lg px-6 py-5 hover:border-military/30 hover:shadow-md transition-all duration-300"
                  >
                    <div>
                      <p className="font-serif text-xl text-military-dark group-hover:text-military transition-colors">{b.name}</p>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-military-dark/40 mt-1">{b.product_count} produto(s)</p>
                    </div>
                    <ArrowUpRight size={16} className="text-military-dark/30 group-hover:text-military transition-colors shrink-0" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {launches.length > 0 && (
        <section id="lancamentos" data-testid="home-launches-section" className="bg-white py-24 md:py-32 border-b border-military/10">
          <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
            <SectionHead
              kicker="Lançamentos"
              title={<>Acabou de <span className="italic font-light text-military">chegar.</span></>}
              linkTo="/produtos?categoria=Todos"
              linkLabel="Ver todos os produtos"
              testid="home-launches-all"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {launches.map((p, i) => <ProductCard key={p.slug} p={p} i={i} />)}
            </div>
          </div>
        </section>
      )}

      {offers.length > 0 && (
        <section id="ofertas" data-testid="home-offers-section" className="bg-baby/40 py-24 md:py-32 border-b border-military/10">
          <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
            <SectionHead
              kicker="Ofertas"
              title={<>Preço que cabe <span className="italic font-light text-military">no bolso.</span></>}
              note="Promoções por tempo limitado, enquanto durar o estoque."
              linkTo="/produtos?ofertas=1"
              linkLabel="Ver todas as ofertas"
              testid="home-offers-all"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {offers.map((p, i) => <ProductCard key={p.slug} p={p} i={i} />)}
            </div>
          </div>
        </section>
      )}

      <section data-testid="home-all-products-cta" className="bg-military-dark grain py-20 md:py-28">
        <div className="w-full max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-serif text-3xl md:text-5xl text-offwhite tracking-tight max-w-xl">
            Explore o catálogo <span className="italic font-light text-baby">completo.</span>
          </h2>
          <Link
            to="/produtos"
            data-testid="home-all-products-button"
            className="group inline-flex items-center gap-3 rounded-full bg-baby text-military-dark px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-offwhite transition-colors duration-300"
          >
            Ver todos os produtos <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </>
  );
};
